import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { X, Play, Sparkles, Youtube, Instagram, Film, Clapperboard } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { fadeUp, staggerParent, viewportOnce } from "@/lib/animations";

const PlatformIcon = ({ type, className }) =>
  type === "youtube" ? <Youtube className={className} /> : type === "instagram" ? <Instagram className={className} /> : <Film className={className} />;

const embedUrl = (v) => {
  if (v.type === "youtube") return `https://www.youtube-nocookie.com/embed/${v.video_id}?autoplay=1&rel=0`;
  if (v.type === "instagram") return `https://www.instagram.com${v.url?.includes("/p/") ? "/p" : "/reel"}/${v.video_id}/embed`;
  return null;
};

const SocialGallery = () => {
  const [data, setData] = useState({ categories: [], videos: [] });
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    api.get("/videos")
      .then((r) => setData(r.data || { categories: [], videos: [] }))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  useEffect(() => {
    if (!playing) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setPlaying(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playing]);

  // Category-wise sections; the category with the newest video floats to the top.
  const known = new Set(data.categories.map((c) => c.name));
  const sections = data.categories
    .map((c) => ({ name: c.name, videos: data.videos.filter((v) => v.category === c.name) }))
    .filter((s) => s.videos.length)
    .map((s) => ({ ...s, latest: Math.max(...s.videos.map((v) => new Date(v.created_at || 0).getTime())) }))
    .sort((a, b) => b.latest - a.latest);
  const other = data.videos.filter((v) => !known.has(v.category));
  if (other.length) sections.push({ name: "More Videos", videos: other, latest: 0 });

  return (
    <div data-testid="social-gallery-page" className="bg-brand-cream">
      <section className="relative overflow-hidden pb-12 pt-32 sm:pt-36 lg:pt-40">
        <div className="pointer-events-none absolute -left-40 top-10 h-[30rem] w-[30rem] rounded-full bg-brand-magenta/10 blur-[120px]" />
        <div className="pointer-events-none absolute -right-40 top-40 h-[30rem] w-[30rem] rounded-full bg-brand-blue/10 blur-[120px]" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <motion.nav initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="mb-7 flex flex-wrap items-center gap-2 font-body text-sm text-brand-ink/50" data-testid="social-gallery-breadcrumb">
            <Link to="/" className="transition-colors duration-300 hover:text-brand-magenta">Home</Link>
            <span>/</span>
            <Link to="/media-press" className="transition-colors duration-300 hover:text-brand-magenta">Media &amp; Press</Link>
            <span>/</span>
            <span className="text-brand-ink/80">Social Media Gallery</span>
          </motion.nav>

          <motion.div variants={staggerParent} initial="hidden" animate="show" className="max-w-3xl">
            <motion.p variants={fadeUp} className="mb-5 inline-flex items-center gap-2 rounded-full border border-brand-line bg-white/60 px-4 py-2 font-heading text-xs font-semibold uppercase tracking-[0.18em] text-brand-ink/70 backdrop-blur">
              <Clapperboard className="h-3.5 w-3.5 text-brand-magenta" /> Watch Us in Action
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-[2.7rem] font-semibold leading-[0.98] tracking-tight text-brand-ink sm:text-6xl lg:text-[4.5rem]">
              Social Media <span className="brand-gradient-text">Gallery</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-6 max-w-xl font-body text-base leading-relaxed text-brand-ink/70 sm:text-lg">
              Real moments from our sessions — reels and videos of teams playing, creating, breathing and transforming. Press play, right here.
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="pb-24 sm:pb-32" data-testid="social-gallery-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          {loaded && data.videos.length === 0 && (
            <div className="flex flex-col items-center rounded-[28px] border border-dashed border-brand-line bg-white/50 px-8 py-20 text-center" data-testid="social-gallery-empty">
              <Play className="mb-4 h-8 w-8 text-brand-ink/30" />
              <p className="font-heading text-xl font-semibold text-brand-ink">Videos coming soon</p>
              <p className="mt-2 font-body text-sm text-brand-ink/55">We are curating our favourite moments — check back shortly.</p>
            </div>
          )}

          {sections.map((s, si) => (
            <div key={s.name} className={si > 0 ? "mt-16" : ""} data-testid={`video-category-section-${si}`}>
              <motion.h2 initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.5 }}
                className="mb-6 font-heading text-2xl font-semibold text-brand-ink sm:text-3xl" data-testid={`video-category-title-${si}`}>
                {s.name}
              </motion.h2>
              <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
                {s.videos.map((v, i) => (
              <motion.button key={v.id} type="button" onClick={() => setPlaying(v)} data-testid={`video-card-${si}-${i}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={viewportOnce} transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                className="group overflow-hidden rounded-[24px] border border-brand-line bg-white text-left transition-shadow duration-300 hover:shadow-xl hover:shadow-brand-magenta/10">
                <span className="relative block aspect-[9/16] overflow-hidden bg-brand-ink">
                  {v.thumbnail ? (
                    <img src={mediaUrl(v.thumbnail)} alt={v.title || "Video thumbnail"} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : v.type === "youtube" ? (
                    <img src={`https://img.youtube.com/vi/${v.video_id}/hqdefault.jpg`} alt={v.title || "Video thumbnail"} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  ) : v.type === "file" ? (
                    <video src={mediaUrl(v.url)} preload="metadata" muted playsInline className="h-full w-full object-cover" />
                  ) : (
                    <span className="flex h-full w-full items-center justify-center brand-gradient">
                      <Instagram className="h-12 w-12 text-white/70" />
                    </span>
                  )}
                  <span className="absolute inset-0 bg-gradient-to-t from-brand-ink/50 via-transparent to-transparent" />
                  <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-brand-ink shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:bg-brand-magenta group-hover:text-white" data-testid={`video-play-${si}-${i}`}>
                    <Play className="ml-0.5 h-6 w-6 fill-current" />
                  </span>
                  <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-brand-ink/70 px-3 py-1 font-heading text-[10px] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur">
                    <PlatformIcon type={v.type} className="h-3 w-3" />
                    {v.type === "youtube" ? "YouTube" : v.type === "instagram" ? "Reel" : "Video"}
                  </span>
                </span>
                <span className="block px-5 py-4">
                  <span className="block truncate font-heading text-base font-semibold text-brand-ink">{v.title || "Untitled video"}</span>
                  <span className="mt-0.5 block font-body text-xs uppercase tracking-[0.14em] text-brand-ink/50">{v.category}</span>
                </span>
              </motion.button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {playing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-brand-ink/90 p-4 backdrop-blur-sm sm:p-8"
            onClick={() => setPlaying(null)} data-testid="video-player-modal">
            <button type="button" aria-label="Close" data-testid="video-player-close"
              className="absolute right-5 top-5 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white transition-colors duration-300 hover:bg-brand-magenta">
              <X className="h-5 w-5" />
            </button>
            <motion.div initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 8 }} transition={{ duration: 0.3 }}
              className={`w-full ${playing.type === "instagram" ? "max-w-md" : "max-w-4xl"}`} onClick={(e) => e.stopPropagation()}>
              <div className={`overflow-hidden rounded-2xl bg-black ${playing.type === "instagram" ? "aspect-[9/16] max-h-[80vh]" : "aspect-video"}`}>
                {playing.type === "file" ? (
                  <video src={mediaUrl(playing.url)} controls autoPlay playsInline className="h-full w-full" data-testid="video-player-file" />
                ) : (
                  <iframe src={embedUrl(playing)} title={playing.title || "Video player"} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen
                    className="h-full w-full" data-testid="video-player-iframe" />
                )}
              </div>
              {playing.title && <p className="mt-4 text-center font-body text-sm text-white/80">{playing.title}</p>}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SocialGallery;
